import { useContext } from 'react';
import { useStore as useZustandStore } from 'zustand';

import StoreContext from '../contexts/EditorContext';

// uncomment if you don't want to go through in the import flow everytime
import mock from './mock.json';
import { AdElement } from '@adflow/types';
import { createEditorStore } from '../store';

type EditorStoreApi = ReturnType<typeof createEditorStore>;

type ExtractState<S> = S extends {
  getState: () => infer T;
}
  ? T
  : never;

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const useMock = urlParams.has('useMock');

export function useStore<StateSlice = ExtractState<EditorStoreApi>>(
  selector: (state: ExtractState<EditorStoreApi>) => StateSlice,
  equalityFn?: (a: StateSlice, b: StateSlice) => boolean,
) {
  const ctx = useContext(StoreContext);

  if (ctx.store === null) {
    throw new Error('Expected EditorContext but got null.');
  }

  const isCypress =
    typeof window !== 'undefined' && Boolean((window as unknown as { Cypress?: unknown }).Cypress);

  if (import.meta.env.DEV || isCypress) {
    // Always overwrite so remounts (Strict Mode / user hydration) keep Cypress
    // pointed at the store the UI is actually using.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__STORE__ = ctx.store;
    // uncomment if you don't want to go through in the import flow everytime
    if (import.meta.env.DEV && !ctx.store.getState().elements.length && useMock) {
      ctx.store.getState().addElements(mock as AdElement[]);
    }
  }

  return useZustandStore(ctx.store, selector, equalityFn);
}
